import { getInventoryConfig } from "@/api/inventory/getConfig";
import { useClearCacheMutation } from "@/api/inventory/nft-cache";
import { ErrorBoundary } from "@/shared/suspense/ErrorBoundary";
import { LoadingFallback } from "@/shared/suspense/LoadingFallback";
import {
	Anchor,
	Box,
	Button,
	Card,
	Divider,
	Group,
	Image,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/")({
	loader: async ({ context }) => {
		await context.queryClient.ensureQueryData(getInventoryConfig());
	},
	errorComponent: ({ error }) => <ErrorBoundary error={error} />,
	pendingComponent: () => <LoadingFallback />,
	component: RouteComponent,
});

export function RouteComponent() {
	const config = useSuspenseQuery(getInventoryConfig());
	const clearCacheMutation = useClearCacheMutation();
	const collections = config.data?.collections ?? {};

	return (
		<div>
			<Title order={2} mb="xl">
				Your Collections
			</Title>
			{!config.data && (
				<Box mb={"lg"}>
					<Text>
						<a href={`${import.meta.env.VITE_API_URL}/auth/login`}>Login</a> to
						view your collections
					</Text>
				</Box>
			)}
			<Group gap="md">
				{Object.values(collections).map((c) => (
					<Card
						key={c.name}
						withBorder
						radius="md"
						p={0}
						style={{ maxWidth: "fit-content" }}
					>
						<Link
							to="/$collection/nfts"
							params={{ collection: c.address }}
							search={{
								filter: "inventory",
							}}
							style={{ textDecoration: "none", color: "inherit" }}
							viewTransition={false}
						>
							<Group wrap="nowrap" gap={0}>
								<Box w={120} h={120}>
									<Image
										src={`/${c.name.toLowerCase().replaceAll(" ", "-")}.webp`}
										fallbackSrc={`https://placehold.co/100x100/e9e8ed/717089?text=${c.name}`}
										alt={c.name}
										style={{
											clipPath: "polygon(0 0, 80% 0, 100% 100%, 0% 100%)",
										}}
										h={120}
										w={120}
									/>
								</Box>
								<Box ml="md" mr="xl">
									<Title order={3} mb={{ base: 0, sm: "md" }}>
										{c.name}
									</Title>
									<Group gap="xs">
										<pre
											style={{
												margin: 0,
												wordBreak: "break-all",
												whiteSpace: "pre-wrap",
												fontSize: "14px",
											}}
										>
											{c.address}
										</pre>
										<Anchor
											href={`https://basescan.org/address/${c.address}`}
											target="_blank"
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											<IconExternalLink size={16} />
										</Anchor>
									</Group>
								</Box>
							</Group>
						</Link>
					</Card>
				))}
			</Group>

			<Divider my="xl" />
			<Link
				to="/marketplace"
				style={{ textDecoration: "none" }}
				viewTransition={false}
			>
				<Title order={3} my="md">
					Shiny Marketplace
				</Title>
			</Link>

			<Divider my="xl" />
			<Link
				to="/balances"
				style={{ textDecoration: "none" }}
				viewTransition={false}
			>
				<Title order={3} my="md">
					Shiny Accounts Overview
				</Title>
			</Link>

			<Divider my="xl" />
			<Title order={3} my="md">
				Guides
			</Title>
			<Stack mb={"md"} gap={"md"}>
				<a
					href="https://scribehow.com/shared/How_To_Create_And_Burn_Box_NFTs__yBlTyeMPSi6fzji_rpL-ow"
					rel="noopener noreferrer"
					target="_blank"
					style={{ textDecoration: "none" }}
				>
					Creating a Box NFT
				</a>
				<a
					href="https://scribehow.com/shared/How_To_Create_And_Burn_Card_NFTs__imdvP-JuQFOh71P1SCKRLA"
					rel="noopener noreferrer"
					target="_blank"
					style={{ textDecoration: "none" }}
				>
					Creating a Card NFT
				</a>
			</Stack>

			<Divider />
			<Title order={3} my="md">
				Troubleshooting
			</Title>
			<Text mb={"md"}>
				This action will clear all cached data, <br />
				ensuring you see the most up-to-date information both in this admin
				dashboard and the Shiny app.
				<br />
				<br /> Useful in these scenarios:
			</Text>
			<ul>
				<li>
					After minting or burning NFTs: Refreshes the NFT inventory display in
					both the admin dashboard and Shiny.
				</li>
				<li>
					After updating NFT metadata: Ensures all NFT details (descriptions,
					attributes, etc.) are updated on Shiny also.
				</li>
				<li>
					After price changes: Ensures all NFT prices are updated on Shiny also.
				</li>
			</ul>

			<Button
				variant="outline"
				color="red"
				mb={"md"}
				loading={clearCacheMutation.isPending}
				disabled={clearCacheMutation.isPending || clearCacheMutation.isSuccess}
				onClick={() => clearCacheMutation.mutate()}
			>
				Clear All Cache
			</Button>
			<br />
			<br />
			<br />
			<br />
		</div>
	);
}
